import { useMemo } from 'react';

import { calculateIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';

import type { Holding } from '@/hooks/portfolio/usePortfolio/types';

import { useHoldingsSorting } from '@/hooks/portfolio/useHoldingsSorting';
import type { HoldingsSorting } from '@/hooks/portfolio/useHoldingsSorting';

type SortingFunction<T> = (a: T, b: T) => -1 | 0 | 1;

export const sortValue = <Value extends string | number>(
  a: Value | null,
  b: Value | null,
  direction: 'asc' | 'desc'
) => {
  if (a === b) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  if (direction === 'desc') {
    return a < b ? 1 : -1;
  }
  return a < b ? -1 : 1;
};

const sortingFns: Record<
  HoldingsSorting,
  SortingFunction<
    Pick<Holding, 'analysis'> & {
      company: Pick<
        Holding['company'],
        'name' | 'tickerSymbol' | 'score' | 'analysisValue' | 'defaultNarrative'
      > & {
        myValuation?: {
          fairValue: number | null;
          narrative: {
            draftUpdate: {
              valuation: {
                fairValue: number | null;
              };
            } | null;
          } | null;
        } | null;
      };
    }
  >
> = {
  'value-desc': (
    { analysis: { valueUSD: a } },
    { analysis: { valueUSD: b } }
  ) => sortValue(a, b, 'desc'),
  'roi-desc': ({ analysis: { roi: a } }, { analysis: { roi: b } }) =>
    sortValue(a, b, 'desc'),
  'company-name-a-to-z': ({ company: { name: a } }, { company: { name: b } }) =>
    sortValue(a.toUpperCase(), b.toUpperCase(), 'asc'),
  'ticker-a-to-z': (
    { company: { tickerSymbol: a } },
    { company: { tickerSymbol: b } }
  ) => sortValue(a.toUpperCase(), b.toUpperCase(), 'asc'),
  'market-cap-desc': (a, b) =>
    sortValue(
      a.company.analysisValue?.marketCapUSD ?? null,
      b.company.analysisValue?.marketCapUSD ?? null,
      'desc'
    ),
  'performance-7d-desc': (
    { analysis: { return7d: a } },
    { analysis: { return7d: b } }
  ) => sortValue(a, b, 'desc'),
  'discount-desc': (a, b) => {
    return sortValue(
      calculateIntrinsicDiscount(
        a.company.analysisValue?.lastSharePrice ?? null,
        a.company.analysisValue?.npvPerShare ?? null,
        a.company?.myValuation?.narrative?.draftUpdate?.valuation?.fairValue ??
          a.company?.myValuation?.fairValue ??
          a.company.defaultNarrative?.valuation.fairValue
      ),
      calculateIntrinsicDiscount(
        b.company.analysisValue?.lastSharePrice ?? null,
        b.company.analysisValue?.npvPerShare ?? null,
        b.company?.myValuation?.narrative?.draftUpdate?.valuation?.fairValue ??
          b.company?.myValuation?.fairValue ??
          b.company.defaultNarrative?.valuation.fairValue
      ),
      'desc'
    );
  },
  'discount-asc': (a, b) =>
    sortValue(
      calculateIntrinsicDiscount(
        a.company.analysisValue?.lastSharePrice ?? null,
        a.company.analysisValue?.npvPerShare ?? null,
        a.company?.myValuation?.narrative?.draftUpdate?.valuation?.fairValue ??
          a.company?.myValuation?.fairValue ??
          a.company.defaultNarrative?.valuation.fairValue
      ),
      calculateIntrinsicDiscount(
        b.company.analysisValue?.lastSharePrice ?? null,
        b.company.analysisValue?.npvPerShare ?? null,
        b.company?.myValuation?.narrative?.draftUpdate?.valuation?.fairValue ??
          b.company?.myValuation?.fairValue ??
          b.company.defaultNarrative?.valuation.fairValue
      ),
      'asc'
    ),
  'total-score-desc': ({ company: { score: a } }, { company: { score: b } }) =>
    sortValue(a?.total || 0, b?.total || 0, 'desc'),
  'value-score-desc': ({ company: { score: a } }, { company: { score: b } }) =>
    sortValue(a?.value || 0, b?.value || 0, 'desc'),
  'future-score-desc': ({ company: { score: a } }, { company: { score: b } }) =>
    sortValue(a?.future || 0, b?.future || 0, 'desc'),
  'past-score-desc': ({ company: { score: a } }, { company: { score: b } }) =>
    sortValue(a?.past || 0, b?.past || 0, 'desc'),
  'dividend-score-desc': (
    { company: { score: a } },
    { company: { score: b } }
  ) => sortValue(a?.dividend || 0, b?.dividend || 0, 'desc'),
  'health-score-desc': ({ company: { score: a } }, { company: { score: b } }) =>
    sortValue(a?.health || 0, b?.health || 0, 'desc'),
  'cash-return-desc': (
    { analysis: { roiValue: a } },
    { analysis: { roiValue: b } }
  ) => sortValue(a, b, 'desc'),
};

export const useSortedHoldings = (holdings: Holding[] = []) => {
  const [sorting] = useHoldingsSorting();

  return useMemo(() => {
    const sortingFn = sortingFns[sorting];
    if (typeof sortingFn === 'undefined') {
      return holdings;
    }
    return [...holdings].sort(sortingFn);
  }, [holdings, sorting]);
};
